import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// Extract a human-readable message from either a plain string detail or
// a Pydantic validation error array like [{msg, loc, ...}].
function apiErr(e, fallback = "Something went wrong") {
    const d = e?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
        return d.map((x) => x?.msg || String(x)).join(" · ") || fallback;
    }
    return fallback;
}
import {
    Plus, Trash, MapPin, Star, StarHalf, User, Phone, Envelope, Link,
    Warning, ArrowsClockwise,
} from "@phosphor-icons/react";
import { IN_CITIES, IN_STATES, stateForCity } from "@/lib/indiaGeo";

/**
 * ClientLocationsSection — V1.5.5.
 *
 * Manages a client's locations + SPOCs (max 2 SPOCs per location) inline
 * on the Client create/edit modal.
 *
 * Props:
 *   clientId?  — if present, fetches + persists locations against the API
 *                immediately. If missing (unsaved client), section shows a
 *                "Save the client first, then add locations" hint.
 */

const DESIGNATIONS = [
    "HR Manager", "Hiring Manager", "Technical Lead", "Recruiter",
    "Interview Coordinator", "Site Head", "Other",
];

const emptyLoc = () => ({
    label: "", address_line_1: "", address_line_2: "",
    city: "", state: "", country: "IN", pincode: "",
    google_maps_url: "", timezone: "Asia/Kolkata", is_primary: false,
});
const emptySpoc = () => ({
    name: "", designation: "HR Manager", phone: "", email: "", is_primary: false,
});

export default function ClientLocationsSection({ clientId }) {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingLoc, setAddingLoc] = useState(false);
    const [locDraft, setLocDraft] = useState(null);      // {loc obj} in edit
    const [spocDraft, setSpocDraft] = useState(null);    // { lid, spoc: {} }

    const load = useCallback(async () => {
        if (!clientId) { setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await api.get(`/clients/${clientId}/locations`);
            setLocations(data || []);
        } catch (e) {
            toast.error(apiErr(e, "Failed to load locations"));
        } finally {
            setLoading(false);
        }
    }, [clientId]);
    useEffect(() => { load(); }, [load]);

    if (!clientId) {
        return (
            <div data-testid="client-locations-section-empty"
                 className="border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-4 text-center">
                <MapPin size={20} className="mx-auto text-[#A3A3A3] mb-1" />
                <p className="text-xs text-[#525252] font-bold">Locations & SPOCs</p>
                <p className="text-[11px] text-[#A3A3A3] mt-0.5">
                    Save this client first — then add offices + points-of-contact.
                </p>
            </div>
        );
    }

    // ── Location CRUD ───────────────────────────────────────────────
    const saveLocation = async (loc) => {
        try {
            if (loc.id) {
                await api.patch(`/clients/${clientId}/locations/${loc.id}`, loc);
                toast.success("Location updated");
            } else {
                await api.post(`/clients/${clientId}/locations`, loc);
                toast.success("Location added");
            }
            setLocDraft(null); setAddingLoc(false);
            await load();
        } catch (e) {
            toast.error(apiErr(e, "Save failed"));
        }
    };
    const removeLocation = async (lid) => {
        if (!window.confirm("Remove this location and all its SPOCs?")) return;
        try {
            await api.delete(`/clients/${clientId}/locations/${lid}`);
            toast.success("Location removed");
            await load();
        } catch (e) { toast.error(apiErr(e, "Delete failed")); }
    };

    // ── SPOC CRUD ───────────────────────────────────────────────────
    const saveSpoc = async (lid, spoc) => {
        try {
            if (spoc.id) {
                await api.patch(`/clients/${clientId}/locations/${lid}/spocs/${spoc.id}`, spoc);
            } else {
                await api.post(`/clients/${clientId}/locations/${lid}/spocs`, spoc);
            }
            toast.success("SPOC saved");
            setSpocDraft(null);
            await load();
        } catch (e) { toast.error(apiErr(e, "Save failed")); }
    };
    const removeSpoc = async (lid, sid) => {
        if (!window.confirm("Remove this SPOC?")) return;
        try {
            await api.delete(`/clients/${clientId}/locations/${lid}/spocs/${sid}`);
            toast.success("SPOC removed");
            await load();
        } catch (e) { toast.error(apiErr(e, "Delete failed")); }
    };

    return (
        <div data-testid="client-locations-section" className="border-t border-[#E5E5E5] pt-4 mt-2 space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                    <MapPin size={11} weight="fill" className="inline mr-1" />
                    Locations & SPOCs
                </h3>
                {!addingLoc && (
                    <button
                        type="button"
                        data-testid="client-add-location-btn"
                        onClick={() => { setAddingLoc(true); setLocDraft(emptyLoc()); }}
                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1"
                    >
                        <Plus size={11} weight="bold" /> Add location
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-4 text-[11px] text-[#A3A3A3]">
                    <ArrowsClockwise className="animate-spin mx-auto mb-1" size={14} /> Loading…
                </div>
            ) : locations.length === 0 && !addingLoc ? (
                <div className="text-[11px] text-[#A3A3A3] bg-[#FAFAFA] border border-dashed border-[#E5E5E5] p-4 text-center">
                    No offices yet. Click <b>Add location</b> to create the first one.
                </div>
            ) : null}

            {locations.map((loc) => (
                <LocationCard
                    key={loc.id}
                    loc={loc}
                    editing={locDraft?.id === loc.id}
                    onEdit={() => setLocDraft({ ...loc })}
                    onCancelEdit={() => setLocDraft(null)}
                    onSave={saveLocation}
                    onRemove={() => removeLocation(loc.id)}
                    onAddSpoc={() => setSpocDraft({ lid: loc.id, spoc: emptySpoc() })}
                    onEditSpoc={(s) => setSpocDraft({ lid: loc.id, spoc: { ...s } })}
                    onRemoveSpoc={(sid) => removeSpoc(loc.id, sid)}
                    spocDraft={spocDraft?.lid === loc.id ? spocDraft.spoc : null}
                    onSaveSpoc={(s) => saveSpoc(loc.id, s)}
                    onCancelSpoc={() => setSpocDraft(null)}
                    setLocDraft={setLocDraft}
                    locDraft={locDraft}
                    setSpocDraft={setSpocDraft}
                />
            ))}

            {addingLoc && (
                <LocationEditor
                    value={locDraft || emptyLoc()}
                    onChange={setLocDraft}
                    onSubmit={() => saveLocation(locDraft)}
                    onCancel={() => { setAddingLoc(false); setLocDraft(null); }}
                    isNew
                />
            )}
        </div>
    );
}

// ── LocationCard (view + expandable SPOC list) ─────────────────────────
function LocationCard({
    loc, editing, onEdit, onCancelEdit, onSave, onRemove,
    onAddSpoc, onEditSpoc, onRemoveSpoc, spocDraft, onSaveSpoc, onCancelSpoc,
    setLocDraft, locDraft, setSpocDraft,
}) {
    const spocs = loc.spocs || [];
    const atMax = spocs.length >= 2;

    return (
        <div data-testid={`client-location-card-${loc.id}`}
             className="border border-[#E5E5E5] p-3 bg-white">
            {editing ? (
                <LocationEditor
                    value={locDraft}
                    onChange={setLocDraft}
                    onSubmit={() => onSave(locDraft)}
                    onCancel={onCancelEdit}
                />
            ) : (
                <div className="flex items-start gap-3">
                    <MapPin size={16} weight="fill" className="text-cobalt mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-ink flex items-center gap-2">
                            {loc.label}
                            {loc.is_primary && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5">
                                    <Star size={9} weight="fill" /> Primary
                                </span>
                            )}
                        </p>
                        <p className="text-[11px] text-[#525252] mt-0.5">
                            {loc.address_line_1}
                            {loc.address_line_2 && `, ${loc.address_line_2}`}
                            {loc.city && `, ${loc.city}`}
                            {loc.state && `, ${loc.state}`}
                            {loc.pincode && ` ${loc.pincode}`}
                        </p>
                        {loc.google_maps_url && (
                            <a
                                href={loc.google_maps_url}
                                target="_blank" rel="noopener noreferrer"
                                className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-cobalt hover:underline"
                            >
                                <Link size={10} weight="bold" /> Open in Google Maps
                            </a>
                        )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button type="button" onClick={onEdit}
                                data-testid={`client-loc-edit-${loc.id}`}
                                className="text-[10px] text-cobalt hover:underline font-bold uppercase tracking-wider">
                            Edit
                        </button>
                        <button type="button" onClick={onRemove}
                                data-testid={`client-loc-delete-${loc.id}`}
                                className="text-red-600 hover:text-red-800 p-1">
                            <Trash size={12} weight="bold" />
                        </button>
                    </div>
                </div>
            )}

            {/* SPOCs block */}
            {!editing && (
                <div className="mt-3 pl-6 space-y-2 border-l-2 border-[#F5F5F5]">
                    <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#A3A3A3]">
                        SPOCs · {spocs.length}/2
                    </p>
                    {spocs.map((s) => (
                        <SpocRow key={s.id} spoc={s} lid={loc.id}
                                 editing={spocDraft?.id === s.id}
                                 draft={spocDraft}
                                 onEdit={() => onEditSpoc(s)}
                                 onRemove={() => onRemoveSpoc(s.id)}
                                 onSave={onSaveSpoc}
                                 onCancel={onCancelSpoc}
                                 setSpocDraft={setSpocDraft}
                        />
                    ))}
                    {/* New SPOC editor */}
                    {spocDraft && spocDraft && spocDraft.id === undefined && (
                        <SpocEditor value={spocDraft} onChange={(s) => setSpocDraft({ lid: loc.id, spoc: s })}
                                    onSubmit={() => onSaveSpoc(spocDraft)}
                                    onCancel={onCancelSpoc} />
                    )}
                    {!atMax && !spocDraft && (
                        <button type="button"
                                data-testid={`client-add-spoc-${loc.id}`}
                                onClick={onAddSpoc}
                                className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1 mt-1">
                            <Plus size={10} weight="bold" /> Add SPOC
                        </button>
                    )}
                    {atMax && (
                        <p className="text-[10px] text-amber-700 flex items-center gap-1 mt-1">
                            <Warning size={10} weight="fill" />
                            Max 2 SPOCs per location. Remove one to add another.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

// ── SPOC row (compact display + edit) ──────────────────────────────────
function SpocRow({ spoc, editing, draft, onEdit, onRemove, onSave, onCancel, setSpocDraft, lid }) {
    if (editing) {
        return (
            <SpocEditor value={draft?.spoc || spoc} onChange={(s) => setSpocDraft({ lid, spoc: s })}
                        onSubmit={() => onSave(draft?.spoc)}
                        onCancel={onCancel} />
        );
    }
    return (
        <div className="flex items-center justify-between text-[11px]">
            <div className="min-w-0 flex-1">
                <p className="font-bold text-ink flex items-center gap-2">
                    <User size={10} weight="fill" className="text-[#525252]" />
                    {spoc.name}
                    <span className="text-[#A3A3A3] font-normal">· {spoc.designation}</span>
                    {spoc.is_primary && (
                        <StarHalf size={10} weight="fill" className="text-amber-500" />
                    )}
                </p>
                <p className="text-[10px] text-[#525252] mt-0.5 pl-4 flex items-center gap-3">
                    <span className="inline-flex items-center gap-0.5">
                        <Phone size={9} weight="fill" /> {spoc.phone}
                    </span>
                    <span className="inline-flex items-center gap-0.5">
                        <Envelope size={9} weight="fill" /> {spoc.email}
                    </span>
                </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={onEdit}
                        className="text-[10px] text-cobalt hover:underline font-bold uppercase">
                    Edit
                </button>
                <button type="button" onClick={onRemove}
                        className="text-red-500 hover:text-red-700 p-0.5">
                    <Trash size={10} weight="bold" />
                </button>
            </div>
        </div>
    );
}

// ── Location editor form ───────────────────────────────────────────────
function LocationEditor({ value, onChange, onSubmit, onCancel, isNew }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    // V1.5.5.1 — City → State cascade. When user picks/types a known city
    // (e.g. Jaipur), auto-fill State (Rajasthan) unless they've manually
    // overridden it. We overwrite the State whenever the city cascade resolves
    // — recruiter admins pointed out mismatched pairs (Jaipur + Karnataka)
    // caused garbage-in downstream.
    const setCity = (v) => {
        const nextState = stateForCity(v);
        onChange({
            ...value,
            city: v,
            // Only overwrite state if we resolved a canonical match. Free-text
            // cities (e.g. "Nellore") leave the existing state alone.
            state: nextState || value.state || "",
        });
    };
    const disabled = !value?.label || !value?.address_line_1 || !value?.city;
    return (
        <div className="space-y-2 bg-[#FAFAFA] border border-[#E5E5E5] p-3">
            <p className="text-[10px] font-black uppercase tracking-[0.15em] text-cobalt">
                {isNew ? "New location" : "Editing location"}
            </p>
            <input type="text" placeholder="Label (e.g. Bengaluru HQ)*"
                   data-testid="loc-label-input"
                   value={value.label || ""} onChange={(e) => set("label", e.target.value)}
                   className="w-full text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
            <input type="text" placeholder="Address line 1*"
                   data-testid="loc-addr1-input"
                   value={value.address_line_1 || ""} onChange={(e) => set("address_line_1", e.target.value)}
                   className="w-full text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
            <input type="text" placeholder="Address line 2 (optional)"
                   value={value.address_line_2 || ""} onChange={(e) => set("address_line_2", e.target.value)}
                   className="w-full text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
            <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="City*"
                       data-testid="loc-city-input"
                       list="in-city-list"
                       value={value.city || ""} onChange={(e) => setCity(e.target.value)}
                       className="text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
                <input type="text" placeholder="State"
                       data-testid="loc-state-input"
                       list="in-state-list"
                       value={value.state || ""} onChange={(e) => set("state", e.target.value)}
                       className="text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
                <datalist id="in-city-list">
                    {IN_CITIES.map((c) => <option key={c} value={c} />)}
                </datalist>
                <datalist id="in-state-list">
                    {IN_STATES.map((s) => <option key={s} value={s} />)}
                </datalist>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Country (IN)"
                       value={value.country || "IN"} onChange={(e) => set("country", e.target.value.toUpperCase().slice(0, 2))}
                       className="text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
                <input type="text" placeholder="Pincode"
                       value={value.pincode || ""} onChange={(e) => set("pincode", e.target.value)}
                       className="text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
            </div>
            <input type="url" placeholder="Google Maps URL (paste from Google Maps)"
                   data-testid="loc-maps-input"
                   value={value.google_maps_url || ""} onChange={(e) => set("google_maps_url", e.target.value)}
                   className="w-full text-sm border border-[#E5E5E5] px-3 py-1.5 focus:outline-none focus:border-cobalt" />
            <label className="inline-flex items-center gap-2 text-[11px] text-[#525252]">
                <input type="checkbox" checked={!!value.is_primary}
                       onChange={(e) => set("is_primary", e.target.checked)} />
                <span>Mark as primary location</span>
            </label>
            <div className="flex items-center gap-2 pt-1">
                <button type="button" onClick={onSubmit} disabled={disabled}
                        data-testid="loc-save-btn"
                        className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-50 text-white text-[10px] font-black uppercase tracking-wider px-4 py-1.5">
                    Save location
                </button>
                <button type="button" onClick={onCancel}
                        className="text-[10px] text-[#525252] hover:text-ink font-bold uppercase tracking-wider">
                    Cancel
                </button>
            </div>
        </div>
    );
}

// ── SPOC editor form ───────────────────────────────────────────────────
function SpocEditor({ value, onChange, onSubmit, onCancel }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    const disabled = !value?.name || !value?.phone || !value?.email;
    return (
        <div className="space-y-2 bg-[#FAFAFA] border border-[#E5E5E5] p-2.5 my-1">
            <p className="text-[10px] font-black uppercase tracking-wider text-cobalt">
                SPOC details
            </p>
            <input type="text" placeholder="Name*"
                   data-testid="spoc-name-input"
                   value={value.name || ""} onChange={(e) => set("name", e.target.value)}
                   className="w-full text-xs border border-[#E5E5E5] px-2.5 py-1.5 focus:outline-none focus:border-cobalt" />
            <div className="grid grid-cols-2 gap-2">
                <select value={value.designation || "HR Manager"}
                        data-testid="spoc-designation-select"
                        onChange={(e) => set("designation", e.target.value)}
                        className="text-xs border border-[#E5E5E5] px-2.5 py-1.5 focus:outline-none focus:border-cobalt">
                    {DESIGNATIONS.map((d) => (<option key={d} value={d}>{d}</option>))}
                </select>
                <input type="tel" placeholder="Phone*"
                       data-testid="spoc-phone-input"
                       value={value.phone || ""} onChange={(e) => set("phone", e.target.value)}
                       className="text-xs border border-[#E5E5E5] px-2.5 py-1.5 focus:outline-none focus:border-cobalt" />
            </div>
            <input type="email" placeholder="Email*"
                   data-testid="spoc-email-input"
                   value={value.email || ""} onChange={(e) => set("email", e.target.value)}
                   className="w-full text-xs border border-[#E5E5E5] px-2.5 py-1.5 focus:outline-none focus:border-cobalt" />
            <label className="inline-flex items-center gap-2 text-[10px] text-[#525252]">
                <input type="checkbox" checked={!!value.is_primary}
                       onChange={(e) => set("is_primary", e.target.checked)} />
                <span>Primary SPOC for this location</span>
            </label>
            <div className="flex items-center gap-2 pt-1">
                <button type="button" onClick={onSubmit} disabled={disabled}
                        data-testid="spoc-save-btn"
                        className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-50 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1">
                    Save SPOC
                </button>
                <button type="button" onClick={onCancel}
                        className="text-[10px] text-[#525252] hover:text-ink font-bold uppercase tracking-wider">
                    Cancel
                </button>
            </div>
        </div>
    );
}
